/**
 * QueueSortableTh — a queue-table column header you can click to sort by, and
 * click again to flip (↑ asc / ↓ desc; ↕ = sortable but inactive). Same idiom
 * and classes as the path-bank table's SortableTh, so the two read alike.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { Info } from 'lucide-react';

import { Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

import { type QueueColumn } from '../queue-columns';
import { type QueueSortState } from './queue-sort';

interface QueueSortableThProps {
  column: QueueColumn;
  sort: QueueSortState;
  onSort: (column: QueueColumn) => void;
}

const QueueSortableTh: FC<QueueSortableThProps> = ({ column, sort, onSort }) => {
  const active = sort.key === column.key;
  return (
    <th
      className={cn(
        'whitespace-nowrap px-2 py-1.5 font-medium',
        column.align === 'right' && 'text-right',
      )}
    >
      <span
        className={cn(
          'inline-flex items-center gap-1',
          column.align === 'right' && 'justify-end',
        )}
      >
        <button
          type="button"
          onClick={() => onSort(column)}
          aria-label={`Sort by ${column.header}${active ? ` (${sort.dir})` : ''}`}
          className={cn(
            'inline-flex items-center gap-1 rounded transition-colors hover:text-foreground',
            active && 'font-semibold text-primary',
          )}
        >
          {column.header}
          <span
            className={cn('text-[10px]', !active && 'text-muted-foreground/50')}
            aria-hidden
          >
            {active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
          </span>
        </button>
        {/* Outside the button on purpose — nesting the tooltip trigger would
         *  toggle the sort every time someone reaches for the explanation. */}
        {column.hint ? (
          <Tooltip content={column.hint} contentClassName="max-w-xs normal-case">
            <Info
              className="h-3 w-3 shrink-0 cursor-help opacity-60"
              aria-label={`About the ${column.header} column`}
            />
          </Tooltip>
        ) : null}
      </span>
    </th>
  );
};

export default QueueSortableTh;
